import {
  users,
  workflowAssignments,
  workflowContexts,
  workflowResponses,
  workflows,
  workflowStages,
} from '@opallabs/api/v3'
import type { WorkflowApi } from '../types'
import { workflowApiV2 } from '../v2'
import {
  selectAssignments,
  selectResponses,
  selectStatus,
  selectWorkflow,
} from './selectors'

const blockFilter = (entityId: string) => ({
  'context.block_id': [entityId],
})

export const workflowApiV3: WorkflowApi = {
  async readWorkflow({ entityId }) {
    const document = await workflows.readCollection({
      params: {
        query: {
          filter: blockFilter(entityId),
          expose: { workflow: { meta: 'true' } },
          include: ['stages'],
        },
      },
    })
    return selectWorkflow(document)
  },

  async readWorkflowStatus({ entityId }) {
    const document = await workflows.readCollection({
      params: {
        query: {
          filter: blockFilter(entityId),
          expose: { workflow: { meta: 'true' } },
          include: ['stages.assignments', 'stages.assignments.responses'],
        },
      },
    })
    return selectStatus(document)
  },

  // Two requests until the server's `POST /v3/workflows/do/create_attached`
  // ships; a failed attach leaves an unattached workflow behind.
  async createAttachedWorkflow({ entityId, firstStage }) {
    const workflow = await workflows.create({
      params: { query: { include: ['stages'], template: firstStage } },
      requestBody: { data: { type: 'workflow' } },
    })
    await workflowContexts.create({
      params: { query: {} },
      requestBody: {
        data: {
          type: 'workflow_context',
          attributes: {},
          relationships: {
            block: { data: { id: entityId, type: 'block' } },
            workflow: { data: { id: workflow.data.id, type: 'workflow' } },
          },
        },
      },
    })
    return { id: workflow.data.id }
  },

  async rewindWorkflow({ workflowId, stageId }) {
    await workflows.rewind({
      params: { path: { workflow_id: workflowId }, query: {} },
      requestBody: {
        data: {
          id: workflowId,
          type: 'workflow',
          relationships: {
            stage: { data: { id: stageId, type: 'workflow_stage' } },
          },
        },
      },
    })
  },

  async createStage({ workflowId, stageType, position }) {
    const document = await workflowStages.create({
      params: { query: {} },
      requestBody: {
        data: {
          type: 'workflow_stage',
          attributes: {
            stage_type: stageType,
            due_date: null,
            position,
            metadata:
              stageType === 'approval' ? { completion_rules: 'all' } : {},
          },
          relationships: {
            workflow: { data: { id: workflowId, type: 'workflow' } },
          },
        },
      },
    })
    return { id: document.data.id }
  },

  async updateStage({ stageId, attributes }) {
    await workflowStages.update({
      params: { path: { workflow_stage_id: stageId }, query: {} },
      requestBody: {
        data: { id: stageId, type: 'workflow_stage', attributes },
      },
    })
  },

  async deleteStage({ stageId }) {
    await workflowStages.delete({
      params: { path: { workflow_stage_id: stageId } },
    })
  },

  async readAssignmentsByStage({ stageId }) {
    const document = await workflowStages.readSingle({
      params: {
        path: { workflow_stage_id: stageId },
        query: { include: ['assignments'] },
      },
    })
    return selectAssignments(document)
  },

  async createAssignment({ stageId, stageType }) {
    await workflowAssignments.create({
      requestBody: {
        data: {
          type: 'workflow_assignment',
          attributes: { assignment_type: stageType },
          relationships: {
            stage: { data: { id: stageId, type: 'workflow_stage' } },
            assigned_user: { data: null },
          },
        },
      },
    })
  },

  async updateAssignment({ assignmentId, attributes = {}, assignedUserId }) {
    const relationships =
      assignedUserId === undefined
        ? undefined
        : {
            assigned_user: {
              data:
                assignedUserId === null
                  ? null
                  : { id: assignedUserId, type: 'user' as const },
            },
          }
    await workflowAssignments.update({
      params: { path: { workflow_assignment_id: assignmentId } },
      requestBody: {
        data: {
          id: assignmentId,
          type: 'workflow_assignment',
          attributes,
          relationships,
        },
      },
    })
  },

  async deleteAssignment({ assignmentId }) {
    await workflowAssignments.delete({
      params: { path: { workflow_assignment_id: assignmentId } },
    })
  },

  assigneeIdOf: (user) => user.uuid,

  async readUser({ userId }) {
    const document = await users.readSingle({
      params: { path: { user_id: userId } },
    })
    // ReadUserV3 doesn't currently take includes, so this has to go through v2
    return workflowApiV2.readUser({
      userId: document.data.attributes.legacy_id,
    })
  },

  async readResponsesByAssignment({ assignmentId, stageType }) {
    const document = await workflowAssignments.readSingle({
      params: {
        path: { workflow_assignment_id: assignmentId },
        query: { include: ['responses'] },
      },
    })
    return selectResponses(document, stageType)
  },

  async createResponse({ assignmentId, details }) {
    await workflowResponses.create({
      params: { query: {} },
      requestBody: {
        data: {
          type: 'workflow_response',
          attributes: { details },
          relationships: {
            assignment: {
              data: { id: assignmentId, type: 'workflow_assignment' },
            },
          },
        },
      },
    })
  },
}
